import type { AnyAction } from "@reduxjs/toolkit"
import { dynamicMiddlewaresInstance, store } from "@core/store"
import { advApi } from "../store/advApi"
import { contentsApi } from "../store/contentsApi"

export interface CachedAssetLink {
  url: string
  expiresAt: number // epoch ms
}

export const ASSET_LINK_FRESH_MARGIN_MS = 30_000

// Memory-only; ключ — пара content.id:asset.id, без владельца материала.
const cache = new Map<string, CachedAssetLink>()
const pending = new Map<string, object>()
const running = new Map<string, Promise<CachedAssetLink>>()

dynamicMiddlewaresInstance.addMiddleware(
  () => (next) => (action: AnyAction) => {
    const previousToken = store.getState().auth.token
    const result = next(action)
    if (
      advApi.util.resetApiState.match(action) ||
      previousToken !== store.getState().auth.token
    ) {
      cache.clear()
      pending.clear()
    }
    return result
  },
)

const cacheKey = (contentId: number, assetId: number): string =>
  `${contentId}:${assetId}`

const isFresh = (entry: CachedAssetLink): boolean =>
  entry.expiresAt - ASSET_LINK_FRESH_MARGIN_MS > Date.now()

export const getAssetLink = async (
  contentId: number,
  assetId: number,
): Promise<CachedAssetLink> => {
  const key = cacheKey(contentId, assetId)
  const cached = cache.get(key)
  if (cached && isFresh(cached)) return cached
  // RTK объединяет параллельные initiate даже с forceRefetch. После очистки
  // ждём старый запрос, чтобы новый потребитель не закешировал его ответ.
  const previous = running.get(key)
  if (previous) {
    if (pending.has(key)) return previous
    await previous.catch(() => undefined)
    return getAssetLink(contentId, assetId)
  }
  const generation = {}
  pending.set(key, generation)
  // Только Map определяет свежесть; временная RTK-подписка живёт до unwrap.
  const request = store.dispatch(
    contentsApi.endpoints.getAssetLink.initiate(
      { pathParams: { contentId, assetId } },
      { forceRefetch: true },
    ),
  )
  const result = (async () => {
    try {
      const link = await request.unwrap()
      const expiresAt = Date.parse(link.expires_at)
      const entry: CachedAssetLink = {
        url: link.url,
        expiresAt: Number.isNaN(expiresAt) ? 0 : expiresAt,
      }
      if (pending.get(key) === generation) cache.set(key, entry)
      return entry
    } finally {
      request.unsubscribe()
      if (pending.get(key) === generation) pending.delete(key)
    }
  })().finally(() => {
    running.delete(key)
  })
  running.set(key, result)
  return result
}

export const removeAsset = (contentId: number, assetId: number): void => {
  const key = cacheKey(contentId, assetId)
  cache.delete(key)
  pending.delete(key)
}

export const removeContent = (contentId: number): void => {
  const prefix = `${contentId}:`
  for (const key of new Set([...cache.keys(), ...pending.keys()])) {
    if (key.startsWith(prefix)) {
      cache.delete(key)
      pending.delete(key)
    }
  }
}
